import { useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { toastStore } from '../../stores/toast-store';
import { TiloTeam } from '../../types/tilo/tilo-game';
import { PlayerRow } from './player-row';

interface TeamCardProps {
  team: TiloTeam;
  nbrPlayersPerTeam: number;
  onAddScore: (teamId: string, score: number) => Promise<void>;
  onGiveToken: (teamId: string, playerId: string) => Promise<void>;
  onKick: (playerId: string) => Promise<void>;
}

export const TeamCard = ({
  team,
  nbrPlayersPerTeam,
  onAddScore,
  onGiveToken,
  onKick
}: TeamCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [scoreInput, setScoreInput] = useState('');

  const handleScore = async () => {
    const val = parseInt(scoreInput, 10);
    if (isNaN(val)) {
      toastStore.show('Invalid', 'Enter a valid number', 'error', 3000);
      return;
    }
    try {
      await onAddScore(team._id, val);
      const msg =
        val > 0
          ? `${val} pts added to ${team.name}`
          : val < 0
          ? `${Math.abs(val)} pts removed from ${team.name}`
          : 'No change';
      toastStore.show('Score Updated', msg, 'success', 3000);
      setScoreInput('');
    } catch {
      toastStore.show('Error', 'Failed to update score', 'error', 3000);
    }
  };

  // Only count/show players who have actually claimed a slot (taken)
  const joinedPlayers = team.players.filter((p) => p.taken);
  const onlinePlayers = joinedPlayers.filter((p) => p.state);
  const offlinePlayers = joinedPlayers.filter((p) => !p.state);

  return (
    <div className={'tilo-team-row'}>
      <div className={'tilo-team-row__main'}>

        {/* Identity */}
        <div className={'tilo-team-row__identity'}>
          <div>
            <div className={'tilo-team-row__name'}>{team.name}</div>
            <div className={'tilo-team-row__code'}>Code: {team.code}</div>
          </div>
          {team.win && <span className={'tilo-badge tilo-badge--won'}>WON</span>}
        </div>

        {/* Stats */}
        <div className={'tilo-team-row__stats'}>
          <div className={'tilo-team-row__stat'}>
            <div className={'tilo-team-row__stat-label'}>Level</div>
            <div className={'tilo-team-row__stat-value'}>{team.level ?? '—'}</div>
          </div>
          <div className={'tilo-team-row__stat'}>
            <div className={'tilo-team-row__stat-label'}>Door</div>
            <div className={'tilo-team-row__stat-value'}>{team.door ?? '—'}</div>
          </div>
          {team.codeEnigma ? (
            <div className={'tilo-team-row__stat'}>
              <div className={'tilo-team-row__stat-label'}>Enigma</div>
              <div className={'tilo-team-row__stat-value'}>{team.codeEnigma}</div>
            </div>
          ) : null}
          {team.rank ? (
            <div className={'tilo-team-row__stat'}>
              <div className={'tilo-team-row__stat-label'}>Rank</div>
              <div className={'tilo-team-row__stat-value'}>#{team.rank}</div>
            </div>
          ) : null}

          {/* Score */}
          <div className={'tilo-team-row__score-group'}>
            <div>
              <div className={'tilo-team-row__score-label'}>Score</div>
              <div className={'tilo-team-row__score-current'}>{team.score}</div>
            </div>
            <input
              type={'number'}
              value={scoreInput}
              onChange={(e) => setScoreInput(e.target.value)}
              placeholder={'±pts'}
              className={'tilo-input tilo-input--xs'}
            />
            <Button size={'small'} themeColor={'primary'} onClick={handleScore}>
              Apply
            </Button>
          </div>

          {/* Players */}
          <div className={'tilo-team-row__players'}>
            <div>
              <div className={'tilo-team-row__score-label'}>Players</div>
              <div className={'tilo-team-row__player-count'}>
                {team.takenPlayers}/{nbrPlayersPerTeam}
              </div>
            </div>
            <div className={'tilo-team-row__dots'}>
              {onlinePlayers.length > 0 && (
                <span
                  className={'d-flex align-items-center gap-1'}
                  style={{ fontSize: 12, color: '#14bf8b', fontWeight: 600 }}>
                  {onlinePlayers.length}
                  <span className={'tilo-dot tilo-dot--online'} />
                </span>
              )}
              {offlinePlayers.length > 0 && (
                <span
                  className={'d-flex align-items-center gap-1'}
                  style={{ fontSize: 12, color: '#dc3545', fontWeight: 600 }}>
                  {offlinePlayers.length}
                  <span className={'tilo-dot tilo-dot--offline'} />
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className={'tilo-team-row__actions'}>
          <button
            className={'tilo-expand-btn'}
            onClick={() => setExpanded((p) => !p)}>
            <span className={'material-symbols-outlined'} style={{ fontSize: 16 }}>
              {expanded ? 'expand_less' : 'expand_more'}
            </span>
            Players
          </button>
        </div>
      </div>

      {expanded && (
        <div className={'tilo-team-row__players-panel'}>
          {team.players.length === 0 ? (
            <p className={'text-muted mb-0'} style={{ fontSize: 12, padding: '4px 0' }}>
              No players yet
            </p>
          ) : (
            <table className={'tilo-players-table'}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Player</th>
                  <th>State</th>
                  <th>Token</th>
                  <th>Give Token</th>
                  <th>Kick Player</th>
                </tr>
              </thead>
              <tbody>
                {team.players.map((player) => (
                  <PlayerRow
                    key={player._id}
                    player={player}
                    teamId={team._id}
                    onGiveToken={onGiveToken}
                    onKick={onKick}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};